import { NgClass, NgFor, NgIf } from '@angular/common';
import {
	Component,
	EventEmitter,
	Input,
	OnChanges,
	OnDestroy,
	Optional,
	Output
} from '@angular/core';
import { MatTooltipModule } from '@angular/material/tooltip';
import { IconComponent, TypographyComponent } from 'components';
import { forkJoin, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import {
	KpiWidget,
	PreviewSlide,
	ReportDateRange,
	SummaryTextWidget,
	TitleWidget
} from '../../../../models/preview/preview.models';
import { ReportContentItem } from '../../../../services/report-settings.service';
import {
	applyContentItemOverrides,
	formatCompactNumber,
	hasPositiveTotal
} from '../../../../services/report-preview-utils';
import {
	AnalyticsInfoResponse,
	AnalyticsVolumeResponse
} from '@brand-lolly/app/social-media-influencer/modules/social-listening/models/social-listening-analytics.model';
import { GetSocialListeningMyBrandAnalyticsVolumeService } from '@brand-lolly/app/social-media-influencer/modules/social-listening/services/get-social-listening-my-brand-analytics-volume.service';
import { ReportSharedReadsService } from '../../../../services/report-shared-reads.service';
import {
	numberSource,
	ReportSource,
	reportSourceDetail,
	sectionSource
} from '../../../../services/report-source';
import { SlideNoDataComponent } from '../slide-no-data/slide-no-data.component';
import { ReportLanguageService } from '../../../../services/report-language.service';
import {
	ReportMessageLanguage,
	reportMessage
} from '../../../../services/report-messages';
import { ReportInlineTextComponent } from '../../components/report-inline-text/report-inline-text.component';
import {
	NO_SLIDE_TEXT_FIELDS,
	ReportSlideTextFields,
	slideTextFields
} from '../../../../services/report-inline-edit.service';
import { ReportSlideBrandComponent } from '../../components/report-slide-brand/report-slide-brand.component';

interface KpiDeltaVm {
	// e.g. "18.4%" — the magnitude only; the arrow carries the direction.
	text: string;
	arrow: '▲' | '▼';
	directionClass: 'slide__kpi-delta--up' | 'slide__kpi-delta--down';
}

export interface KpiCardVm {
	id: string;
	// CSS class carrying the per-metric accent dot color (tokenised in SCSS).
	dotClass: string;
	label: string;
	// "—" when the metric has no available data source (see EXECUTIVE_KPIS).
	value: string;
	// Precomputed (not getters) so the template doesn't recompute per CD pass.
	delta: KpiDeltaVm | null;
	// Trailing muted text next to the delta ("vs. prev period"). Empty for
	// data-gap tiles so no meta row renders.
	suffix: string;
	// Where this one number came from, for its tooltip. Filled in once the
	// brand's name is known; empty until then, which shows no tooltip.
	source: string;
}

// Resolves a KPI's display value + weekly change from the analytics-info
// response. Returns `changes: null` when there's no delta to show.
type KpiResolver = (
	info: AnalyticsInfoResponse,
	language: ReportMessageLanguage
) => {
	value: string;
	/** The count `value` shows, unrounded. */
	amount: number;
	changes: number | null;
	suffix: string;
};

/** A tile's count: shown compact, and kept whole beside it. */
function counted(amount: number): { value: string; amount: number } {
	return { value: formatCompactNumber(amount), amount };
}

// "share 61.3%" suffix for the sentiment-split tiles. Backend share is a
// fraction 0–1 (null when there are no mentions) → "" so no suffix renders.
function sharePct(
	share: number | null,
	language: ReportMessageLanguage
): string {
	return share == null
		? ''
		: reportMessage(language, 'kpi.share', {
				value: (share * 100).toFixed(1)
		  });
}

// The 10 target KPIs in grid order (2 rows × 5 cols). Only the three that map
// cleanly onto AnalyticsInfoResponse are wired; the rest are data gaps.
const EXECUTIVE_KPIS: ReadonlyArray<{
	id: string;
	dotClass: string;
	resolve: KpiResolver;
}> = [
	{
		id: 'mentions',
		dotClass: 'slide__kpi-dot--mentions',
		resolve: (info, language) => ({
			...counted(info.totalMentions),
			changes: info.totalMentionsChanges,
			suffix: reportMessage(language, 'kpi.vs_previous')
		})
	},
	{
		id: 'likes',
		dotClass: 'slide__kpi-dot--likes',
		resolve: (info, language) => ({
			...counted(info.likes),
			changes: info.likesChanges,
			suffix: reportMessage(language, 'kpi.vs_previous')
		})
	},
	{
		id: 'positive_mentions',
		dotClass: 'slide__kpi-dot--positive',
		resolve: (info, language) => ({
			...counted(info.positiveMentions),
			changes: info.positiveMentionsChanges,
			suffix: sharePct(info.positiveShare, language)
		})
	},
	{
		id: 'social_media_reach',
		dotClass: 'slide__kpi-dot--social-reach',
		resolve: (info, language) => ({
			...counted(info.socialMediaReach),
			changes: info.socialMediaReachChanges,
			suffix: reportMessage(language, 'kpi.vs_previous')
		})
	},
	{
		id: 'web_mentions',
		dotClass: 'slide__kpi-dot--web-mentions',
		resolve: (info, language) => ({
			...counted(info.webMentions),
			changes: info.webMentionsChanges,
			suffix: reportMessage(language, 'kpi.vs_previous')
		})
	},
	{
		id: 'reach_total',
		dotClass: 'slide__kpi-dot--reach-total',
		resolve: (info, language) => ({
			...counted(info.totalReach),
			changes: info.totalReachChanges,
			suffix: reportMessage(language, 'kpi.vs_previous')
		})
	},
	{
		id: 'negative_mentions',
		dotClass: 'slide__kpi-dot--negative',
		resolve: (info, language) => ({
			...counted(info.negativeMentions),
			changes: info.negativeMentionsChanges,
			suffix: sharePct(info.negativeShare, language)
		})
	},
	{
		id: 'social_mentions',
		dotClass: 'slide__kpi-dot--social-mentions',
		resolve: (info, language) => ({
			...counted(info.socialMentions),
			changes: info.socialMentionsChanges,
			suffix: reportMessage(language, 'kpi.vs_previous')
		})
	},
	{
		id: 'social_interactions',
		dotClass: 'slide__kpi-dot--social-interactions',
		resolve: (info, language) => ({
			...counted(info.totalEngagements),
			changes: info.totalEngagementsChanges,
			suffix: reportMessage(language, 'kpi.vs_previous')
		})
	},
	{
		// Web-source posts carry no per-mention reach (estimated_reach is 0 for
		// them), so "Web Reach" was always 0. Show distinct web domains instead.
		id: 'web_sites',
		dotClass: 'slide__kpi-dot--web-sites',
		resolve: (info, language) => ({
			...counted(info.webSites),
			changes: info.webSitesChanges,
			suffix: reportMessage(language, 'kpi.vs_previous')
		})
	}
];

// Widget-document delta string (kept for the emitted PreviewSlide). "" when no
// change is available.
function formatChange(value: number | null): string {
	if (!value) return '';
	return value > 0 ? `+${value}%` : `${value}%`;
}

@Component({
	standalone: true,
	selector: 'app-executive-summary-slide',
	templateUrl: './executive-summary-slide.component.html',
	styleUrls: ['./executive-summary-slide.component.scss'],
	imports: [
		NgClass,
		NgFor,
		NgIf,
		TypographyComponent,
		IconComponent,
		SlideNoDataComponent,
		MatTooltipModule,
		ReportInlineTextComponent,
		ReportSlideBrandComponent
	]
})
export class ExecutiveSummarySlideComponent implements OnChanges, OnDestroy {
	@Input() item!: ReportContentItem;
	@Input() brandId!: string;
	@Input() dateRange!: ReportDateRange;
	@Input() themeColor: string | null = null;
	@Input() editorMode = false;
	// CONTRACT: set by the slide host for a report drawn about its source, whose
	// tiles are that source's. Null names the report's brand, read here.
	@Input() source: ReportSource | null = null;
	@Output() slideBuilt = new EventEmitter<PreviewSlide>();

	/** The heading and summary the reader can type into, in the editor. */
	textFields: ReportSlideTextFields = NO_SLIDE_TEXT_FIELDS;

	kpiCards: KpiCardVm[] = [];
	title = '';
	dateLabel = '';
	summaryText = '';
	titleColor = '#2872e1';
	// Precomputed (not a getter) so *ngIf doesn't recompute per CD pass. Starts
	// true so the no-data state shows until the first response lands.
	isEmpty = true;

	private destroy$ = new Subject<void>();
	private cachedInfo: AnalyticsInfoResponse | null = null;
	private cachedVolume: AnalyticsVolumeResponse | null = null;
	private cacheKey: string | null = null;
	private brandLabel: string | null = null;

	constructor(
		private sharedReads: ReportSharedReadsService,
		private analyticsVolumeService: GetSocialListeningMyBrandAnalyticsVolumeService,
		@Optional() private reportLanguage: ReportLanguageService | null
	) {}

	private get language(): ReportMessageLanguage {
		return this.reportLanguage?.language ?? 'en';
	}

	trackByKpiId(_index: number, card: KpiCardVm): string {
		return card.id;
	}

	ngOnChanges(): void {
		this.destroy$.next();
		if (!this.item || !this.brandId || !this.dateRange) return;

		this.sharedReads
			.brandLabel(this.brandId)
			.pipe(takeUntil(this.destroy$))
			.subscribe(label => {
				this.brandLabel = label;
				this.refreshKpiSources();
			});

		const currentCacheKey = `${this.brandId}:${this.dateRange.start}:${this.dateRange.end}`;
		if (
			this.cachedInfo &&
			this.cachedVolume &&
			this.cacheKey === currentCacheKey
		) {
			this.applyData(this.cachedInfo, this.cachedVolume);
			return;
		}

		this.cacheKey = currentCacheKey;

		// Scalar KPI values come from the info endpoint. The volume endpoint is
		// still fetched alongside so the slide is built once with everything the
		// wider report document needs.
		//
		// Both MUST carry the report's window: unscoped, they return all-time
		// totals, which is why the slide showed ~6.5k mentions under a 30-day
		// date label. `cacheKey` above already keyed on the range, so the intent
		// was there — only the requests didn't carry it.
		//
		// The info read is the page's SHARED one: the thin-data gate decides
		// eleven sections by its total mentions, and a second request for the
		// same figure could come back from a different moment and disagree
		// with the tile that prints it.
		const from = new Date(this.dateRange.start);
		const to = new Date(this.dateRange.end);
		forkJoin({
			info: this.sharedReads.kpis(this.brandId, this.dateRange),
			volume: this.analyticsVolumeService.fetch(
				this.brandId,
				from,
				undefined,
				to
			)
		})
			.pipe(takeUntil(this.destroy$))
			.subscribe(({ info, volume }) => {
				this.cachedInfo = info;
				this.cachedVolume = volume;
				this.applyData(info, volume);
			});
	}

	ngOnDestroy(): void {
		this.destroy$.next();
		this.destroy$.complete();
	}

	private applyData(
		info: AnalyticsInfoResponse,
		volume: AnalyticsVolumeResponse
	): void {
		const rawSlide = this.buildSlide(info, volume);
		const slide = applyContentItemOverrides(
			rawSlide,
			this.item,
			this.themeColor
		);
		this.updateViewModel(slide, info);
		this.slideBuilt.emit(slide);
	}

	private buildSlide(
		info: AnalyticsInfoResponse,
		_volume: AnalyticsVolumeResponse
	): PreviewSlide {
		const prefix = `${this.item.id}_${this.brandId}`;

		const language = this.language;
		const kpiWidgets: KpiWidget[] = EXECUTIVE_KPIS.map(def => {
			const resolved = def.resolve(info, language);
			return {
				id: `${prefix}_kpi_${def.id}`,
				kind: 'kpi',
				visible: true,
				content: {
					label: reportMessage(language, `kpi.${def.id}`),
					value: resolved.value,
					amount: resolved.amount,
					delta: formatChange(resolved.changes)
				}
			};
		});

		return {
			id: `${prefix}_slide`,
			slideType: 'executive_summary',
			title: this.item.title || 'Executive Summary',
			description: this.item.description || '',
			dateRange: this.dateRange,
			widgets: [
				{
					id: `${prefix}_title`,
					kind: 'title',
					visible: true,
					content: {
						text: this.item.title || 'Executive Summary',
						color: '#2872e1'
					}
				},
				{
					id: `${prefix}_date`,
					kind: 'date_range',
					visible: true,
					content: { text: this.dateRange.label }
				},
				{
					id: `${prefix}_summary`,
					kind: 'summary_text',
					visible: true,
					content: {
						// TRAP: no stock line drawn about a source — it speaks of
						// "your brand", and a report about a source is about none.
						text:
							this.item.description ||
							(this.source
								? ''
								: 'A high-level overview of your brand performance across all monitored channels.')
					}
				},
				...kpiWidgets
			]
		};
	}

	private updateViewModel(
		slide: PreviewSlide,
		info: AnalyticsInfoResponse
	): void {
		this.textFields = slideTextFields(slide);
		const hidden = new Set(slide.overrides?.hiddenWidgetIds ?? []);
		this.dateLabel =
			this.getTitleWidget(slide, 'date_range', hidden)?.content.text ??
			slide.dateRange?.label ??
			'';
		this.summaryText =
			this.getSummaryWidget(slide, hidden)?.content.text ??
			slide.description ??
			'';
		this.title =
			this.getTitleWidget(slide, 'title', hidden)?.content.text ??
			slide.title;
		this.titleColor =
			this.getTitleWidget(slide, 'title', hidden)?.content.color ??
			'#2872e1';

		// The target design is a fixed 2×5 grid of the 10 executive KPIs, built
		// directly from the info response (not the emitted widgets, which don't
		// carry the dot color / suffix presentation).
		const language = this.language;
		this.kpiCards = EXECUTIVE_KPIS.map(def => {
			const resolved = def.resolve(info, language);
			return {
				id: def.id,
				dotClass: def.dotClass,
				label: reportMessage(language, `kpi.${def.id}`),
				value: resolved.value,
				delta: buildDelta(resolved.changes),
				suffix: resolved.suffix,
				source: ''
			};
		});
		this.refreshKpiSources();

		this.isEmpty = !hasPositiveTotal([
			info.totalMentions,
			info.totalReach,
			info.totalEngagements
		]);
	}

	/**
	 * Each tile's own source: the slide's read, naming that one figure.
	 *
	 * Run from both ends — when the tiles are built and when the brand's name
	 * arrives — because either can land first, and neither should wait for
	 * the other before the slide draws.
	 */
	private refreshKpiSources(): void {
		const source =
			this.source ??
			sectionSource(
				'executive_summary',
				this.brandLabel,
				this.dateRange,
				this.language
			);
		for (const card of this.kpiCards) {
			card.source = source
				? reportSourceDetail(numberSource(source, card.label))
				: '';
		}
	}

	private getTitleWidget(
		slide: PreviewSlide,
		kind: TitleWidget['kind'],
		hidden: Set<string>
	): TitleWidget | undefined {
		return slide.widgets.find(w => w.kind === kind && !hidden.has(w.id)) as
			| TitleWidget
			| undefined;
	}

	private getSummaryWidget(
		slide: PreviewSlide,
		hidden: Set<string>
	): SummaryTextWidget | undefined {
		return slide.widgets.find(
			w => w.kind === 'summary_text' && !hidden.has(w.id)
		) as SummaryTextWidget | undefined;
	}
}

function buildDelta(changes: number | null): KpiDeltaVm | null {
	if (changes === null || changes === undefined || changes === 0) return null;
	const up = changes > 0;
	return {
		text: `${Math.abs(changes)}%`,
		arrow: up ? '▲' : '▼',
		directionClass: up ? 'slide__kpi-delta--up' : 'slide__kpi-delta--down'
	};
}
